import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircleIcon, XCircleIcon, LoaderIcon, HomeIcon, RefreshCwIcon, AlertCircleIcon, MailIcon } from 'lucide-react';
export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const {
    verifyEmail,
    currentUser,
    refreshUser,
    userType,
    sendVerificationEmail
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  // FIXED: Enhanced verification handling - avoid double verification
  useEffect(() => {
    const verifyUserEmail = async () => {
      const actionCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');
      console.log('[VerifyEmail] Legacy page loaded with params:', {
        hasActionCode: !!actionCode,
        mode
      });
      // If no action code, this is likely a redirect back from email
      if (!actionCode) {
        console.log('[VerifyEmail] No action code, redirecting to email-verified page');
        navigate('/email-verified', {
          replace: true
        });
        return;
      }
      // If mode is not verifyEmail, show error
      if (mode !== 'verifyEmail') {
        console.error('[VerifyEmail] Invalid mode:', mode);
        setError('Invalid verification link. Please try again or request a new verification email.');
        setErrorCode('invalid-link');
        setLoading(false);
        return;
      }
      try {
        setVerifying(true);
        console.log('[VerifyEmail] Attempting to verify email with action code');
        // Try to verify the email
        await verifyEmail(actionCode);
        console.log('[VerifyEmail] Email verification successful');
        // Refresh user to get updated emailVerified status
        if (currentUser) {
          await refreshUser();
        }
        setSuccess(true);
        // Redirect to the new email-verified page
        setTimeout(() => {
          navigate('/email-verified', {
            replace: true
          });
        }, 2000);
      } catch (error: any) {
        console.error('[VerifyEmail] Error verifying email:', error);
        // Check if error is because link was already used
        if (error.code === 'auth/invalid-action-code') {
          console.log('[VerifyEmail] Link already used, redirecting to email-verified');
          // Link already used - redirect to email-verified to check status
          navigate('/email-verified', {
            replace: true
          });
          return;
        }
        // Handle other errors
        if (error.code === 'auth/expired-action-code') {
          setError('The verification link has expired. Please request a new verification email.');
          setErrorCode('expired-link');
        } else if (error.code === 'auth/user-not-found') {
          setError('We could not find your account. Please make sure you are using the same email address you signed up with.');
          setErrorCode('user-not-found');
        } else if (error.code === 'auth/network-request-failed') {
          setError('Network error. Please check your internet connection and try again.');
          setErrorCode('network-error');
        } else {
          setError('Failed to verify your email. The link may have expired or is invalid.');
          setErrorCode('verification-failed');
        }
      } finally {
        setLoading(false);
        setVerifying(false);
      }
    };
    verifyUserEmail();
  }, [searchParams, verifyEmail, currentUser, refreshUser, navigate, userType]);
  // FIXED: Enhanced resend verification with better error handling
  const handleResendVerification = async () => {
    if (!currentUser || countdown > 0) return;
    try {
      console.log('[VerifyEmail] Resending verification email');
      await sendVerificationEmail();
      setCountdown(60);
      setError('A new verification email has been sent. Please check your inbox and spam folder.');
      setErrorCode('verification-resent');
    } catch (error: any) {
      console.error('[VerifyEmail] Error resending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        setError('Too many verification emails sent. Please wait a while before trying again.');
      } else if (error.message) {
        setError(`Failed to send verification email: ${error.message}`);
      } else {
        setError('Failed to send verification email. Please try again later.');
      }
      setErrorCode('resend-failed');
    }
  };
  // Handle countdown timer for resend button
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown]);
  // Helper function to determine redirect location
  function getDashboardLink() {
    if (userType === 'teacher') {
      return '/teacher-dashboard';
    } else if (userType === 'student') {
      return '/student-dashboard';
    } else {
      return '/';
    }
  }
  return <div className="min-h-screen flex flex-col bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]" data-id="element-10683">
      <div className="flex-grow flex items-center justify-center px-4 py-12" data-id="element-10684">
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 max-w-md w-full border border-white/20 shadow-xl" data-id="element-10685">
          {loading ? <div className="text-center" data-id="element-10686">
              <LoaderIcon className="h-16 w-16 text-deep-pink mx-auto mb-4 animate-spin" data-id="element-10687" />
              <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-10688">
                Verifying Your Email
              </h1>
              <p className="text-gray-300" data-id="element-10689">
                Please wait while we verify your email address...
              </p>
            </div> : success ? <div className="text-center" data-id="element-10690">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" data-id="element-10691" />
              <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-10692">
                Email Verified Successfully!
              </h1>
              <p className="text-gray-300 mb-8" data-id="element-10693">
                Your email has been verified. Redirecting you now...
              </p>
            </div> : <div className="text-center" data-id="element-10694">
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" data-id="element-10695" />
              <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-10696">
                {errorCode === 'verification-resent' ? 'Email Sent' : 'Verification Issue'}
              </h1>
              <p className="text-gray-300 mb-6" data-id="element-10697">{error}</p>

              {/* Show resend button for certain error codes */}
              {(errorCode === 'expired-link' || errorCode === 'verification-failed') && currentUser && <div className="space-y-4 mb-4" data-id="element-10698">
                    <button onClick={handleResendVerification} disabled={countdown > 0 || verifying} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-6 rounded-lg transition-colors block w-full flex items-center justify-center disabled:opacity-50" data-id="element-10699">
                      {verifying ? <LoaderIcon className="h-5 w-5 animate-spin mr-2" data-id="element-10700" /> : <MailIcon className="h-5 w-5 mr-2" data-id="element-10701" />}
                      {countdown > 0 ? `Resend Email (${countdown}s)` : 'Resend Verification Email'}
                    </button>
                  </div>}

              <div className="space-y-4" data-id="element-10702">
                <a href={currentUser ? '/teacher-email-verification' : '/teacher-login'} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-6 rounded-lg transition-colors block" data-id="element-10703">
                  {currentUser ? 'Back to Verification Page' : 'Back to Login'}
                </a>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};